import { inject, Injectable } from '@angular/core';
import { ApiCallLoggerService } from './api-call-logger.service';
import { environment } from 'src/environments/environment';

/**
 * Response header names (lower-cased) that are treated as trace identifiers.
 * Any header whose name contains "trace" is also picked up automatically.
 */
const TRACE_ID_HEADERS = [
  'x-amzn-trace-id',
  'x-amzn-requestid',
  'x-amz-cf-id',
  'x-request-id',
  'x-trace-id',
  'trace-id',
  'traceid',
  'x-correlation-id',
  'apigw-requestid',
];

interface XhrLogMeta {
  method: string;
  url: string;
  timestamp: string;
  start: number;
  body?: Document | XMLHttpRequestBodyInit | null;
}

type PatchedXhr = XMLHttpRequest & { __apiLogMeta?: XhrLogMeta };

/**
 * Intercepts every outgoing API call made through `fetch` or `XMLHttpRequest`
 * and records url, timestamp, input, error and the trace id from the response
 * headers via {@link ApiCallLoggerService}.
 *
 * Patching the network primitives (rather than using an Angular
 * `HttpInterceptor`) guarantees we also capture calls issued by third-party
 * SDKs such as the AWS AppSync client and the Bot Framework DirectLine client,
 * neither of which routes through Angular's `HttpClient`.
 */
@Injectable({ providedIn: 'root' })
export class NetworkInterceptorService {
  private readonly logger = inject(ApiCallLoggerService);
  private installed = false;

  install(): void {
    if (!environment.enableLogging) {
      return;
    }
    if (this.installed || typeof window === 'undefined') {
      return;
    }
    this.installed = true;
    this.patchFetch();
    this.patchXhr();
  }

  private patchFetch(): void {
    if (typeof window.fetch !== 'function') {
      return;
    }

    const originalFetch = window.fetch.bind(window);
    const logger = this.logger;

    window.fetch = (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> => {
      const url = this.resolveUrl(input);
      const method = (
        init?.method ??
        (input instanceof Request ? input.method : 'GET')
      ).toUpperCase();
      // A Request body is only exposed as a stream, so it must be read
      // asynchronously. Resolve it before firing the request so the clone
      // happens while the body is still unconsumed.
      const bodyPromise = this.resolveRequestBody(input, init);
      const timestamp = new Date().toISOString();
      const start = Date.now();

      return originalFetch(input, init).then(
        (response) => {
          bodyPromise.then((body) =>
            logger.log({
              url,
              method,
              timestamp,
              input: this.parseBody(body),
              status: response.status,
              traceId: this.extractTraceIdFromHeaders(response.headers),
              error:
                response.ok || response.status < 400
                  ? undefined
                  : `HTTP ${response.status} ${response.statusText}`,
              durationMs: Date.now() - start,
              transport: 'fetch',
            }),
          );
          return response;
        },
        (err) => {
          bodyPromise.then((body) =>
            logger.log({
              url,
              method,
              timestamp,
              input: this.parseBody(body),
              status: 0,
              traceId: null,
              error: this.serializeError(err),
              durationMs: Date.now() - start,
              transport: 'fetch',
            }),
          );
          throw err;
        },
      );
    };
  }

  /**
   * Resolves the outgoing request body for logging.
   *
   * `init.body` is available synchronously. When a `Request` object is passed
   * directly (with no `init.body`), its body is only readable as a stream, so
   * the request is cloned and its body read as text without disturbing the
   * request that is actually sent.
   */
  private resolveRequestBody(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<unknown> {
    if (init?.body !== undefined && init?.body !== null) {
      return Promise.resolve(init.body);
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
      try {
        return input
          .clone()
          .text()
          .then((text) => (text ? text : null))
          .catch(() => null);
      } catch {
        return Promise.resolve(null);
      }
    }
    return Promise.resolve(null);
  }

  private patchXhr(): void {
    if (typeof XMLHttpRequest === 'undefined') {
      return;
    }

    const self = this;
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (
      this: PatchedXhr,
      method: string,
      url: string | URL,
      ...rest: any[]
    ): void {
      this.__apiLogMeta = {
        method: (method || 'GET').toUpperCase(),
        url: typeof url === 'string' ? url : url.toString(),
        timestamp: '',
        start: 0,
      };
      return originalOpen.apply(this, [method, url, ...rest] as any);
    };

    XMLHttpRequest.prototype.send = function (
      this: PatchedXhr,
      body?: Document | XMLHttpRequestBodyInit | null,
    ): void {
      const meta = this.__apiLogMeta;
      if (meta) {
        meta.timestamp = new Date().toISOString();
        meta.start = Date.now();
        meta.body = body;

        this.addEventListener('loadend', () => {
          const isError = this.status === 0 || this.status >= 400;
          self.logger.log({
            url: meta.url,
            method: meta.method,
            timestamp: meta.timestamp,
            input: self.parseBody(meta.body),
            status: this.status,
            traceId: self.extractTraceIdFromRawHeaders(
              this.getAllResponseHeaders(),
            ),
            error: isError
              ? this.statusText || `HTTP ${this.status}`
              : undefined,
            durationMs: Date.now() - meta.start,
            transport: 'xhr',
          });
        });
      }
      return originalSend.apply(this, [body] as any);
    };
  }

  private resolveUrl(input: RequestInfo | URL): string {
    if (typeof input === 'string') {
      return input;
    }
    if (input instanceof URL) {
      return input.toString();
    }
    return input.url;
  }

  private extractTraceIdFromHeaders(headers: Headers): string | null {
    if (!headers) {
      return null;
    }
    for (const name of TRACE_ID_HEADERS) {
      const value = headers.get(name);
      if (value) {
        return value;
      }
    }
    // Fallback: any header whose name mentions "trace".
    let traceValue: string | null = null;
    headers.forEach((value, name) => {
      if (!traceValue && name.toLowerCase().includes('trace')) {
        traceValue = value;
      }
    });
    return traceValue;
  }

  private extractTraceIdFromRawHeaders(rawHeaders: string): string | null {
    if (!rawHeaders) {
      return null;
    }
    const map = new Map<string, string>();
    rawHeaders
      .trim()
      .split(/[\r\n]+/)
      .forEach((line) => {
        const index = line.indexOf(':');
        if (index > -1) {
          const key = line.slice(0, index).trim().toLowerCase();
          const value = line.slice(index + 1).trim();
          map.set(key, value);
        }
      });

    for (const name of TRACE_ID_HEADERS) {
      const value = map.get(name);
      if (value) {
        return value;
      }
    }
    for (const [key, value] of map.entries()) {
      if (key.includes('trace') && value) {
        return value;
      }
    }
    return null;
  }

  private parseBody(body: unknown): unknown {
    if (body === null || body === undefined) {
      return null;
    }
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        return body;
      }
    }
    const isBinaryLike =
      (typeof Blob !== 'undefined' && body instanceof Blob) ||
      (typeof FormData !== 'undefined' && body instanceof FormData) ||
      (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer);
    if (isBinaryLike) {
      return `[${(body as object).constructor.name}]`;
    }
    return body;
  }

  private serializeError(err: unknown): unknown {
    if (err instanceof Error) {
      return { name: err.name, message: err.message };
    }
    return err;
  }
}
